/**
 * @fileoverview Card displaying bank connections.
 * @module features/settings/components/BankConnectionsCard
 */

import { ReactElement, useState, useRef, useEffect } from 'react'
import { formatDistanceToNow, parseISO, subDays } from 'date-fns'
import { pl, enUS } from 'date-fns/locale'
import { Landmark, AlertTriangle, RefreshCw, Unplug, Plus, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { motion, useSpring, useMotionValue, AnimatePresence } from 'framer-motion'
import type { DateRange } from 'react-day-picker'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Badge,
  Skeleton,
  Button,
  ConfirmPopover,
  DateRangePicker,
} from '@/shared/components/ui'
import { cn } from '@/shared/utils/cn'
import { toDateOnlyStringLocal } from '@/shared/utils/dateUtils'
import { useIntegrationsStatus } from '../hooks/useIntegrationsStatus'
import {
  useSyncBankConnection,
  useRevokeBankConnection,
  ConnectBankModal,
} from '@/features/bank-connections'
import type { IntegrationStatus, IntegrationProvider } from '../schemas/integrationStatusSchema'
import {
  STATUS_BADGE_VARIANTS,
  STATUS_LABEL_KEYS,
  PROVIDER_NAMES,
} from './integration-shared'
import { getProviderIcon } from './integration-shared'

/** Bank provider filter */
const BANK_PROVIDER: IntegrationProvider = 'BANK'

interface BankEntryProps {
  integration: IntegrationStatus
  isExpanded: boolean
  onToggle: () => void
  onReconnect: () => void
}

/**
 * Bank integration entry with sync/disconnect actions (expandable).
 */
function BankEntry({ integration, isExpanded, onToggle, onReconnect }: BankEntryProps): ReactElement {
  const { t, i18n } = useTranslation('features-settings')
  const contentRef = useRef<HTMLDivElement>(null)
  const heightValue = useMotionValue(0)
  const animatedHeight = useSpring(heightValue, { stiffness: 300, damping: 30 })

  const syncMutation = useSyncBankConnection()
  const revokeMutation = useRevokeBankConnection()

  const [syncDateRange, setSyncDateRange] = useState<DateRange | undefined>(() => ({
    from: subDays(new Date(), 30),
    to: new Date(),
  }))

  const dateLocale = i18n.language === 'pl' ? pl : enUS
  const statusVariant = STATUS_BADGE_VARIANTS[integration.status] ?? STATUS_BADGE_VARIANTS.NOT_CONFIGURED
  const statusLabelKey = STATUS_LABEL_KEYS[integration.status] ?? STATUS_LABEL_KEYS.NOT_CONFIGURED
  const displayName = integration.accountName ?? PROVIDER_NAMES.BANK
  const isActive = integration.status === 'ACTIVE'
  const needsReconnect = integration.status === 'EXPIRED' || integration.status === 'ERROR'

  const lastSyncedLabel = integration.lastSyncedAt
    ? t('cards.integrations.sync.lastSynced', {
        time: formatDistanceToNow(parseISO(integration.lastSyncedAt), {
          addSuffix: true,
          locale: dateLocale,
        }),
      })
    : t('cards.integrations.sync.neverSynced')

  const hasError = integration.status === 'ERROR' && integration.lastSyncError

  useEffect(() => {
    if (contentRef.current) {
      heightValue.set(isExpanded ? contentRef.current.scrollHeight : 0)
    }
  }, [isExpanded, heightValue, syncDateRange])

  const handleSync = (): void => {
    if (!integration.connectionId) return
    const params =
      syncDateRange?.from && syncDateRange?.to
        ? {
            id: integration.connectionId,
            dateFrom: toDateOnlyStringLocal(syncDateRange.from),
            dateTo: toDateOnlyStringLocal(syncDateRange.to),
          }
        : { id: integration.connectionId }
    syncMutation.mutate(params)
  }

  const handleRevoke = async (): Promise<void> => {
    if (!integration.connectionId) return
    await revokeMutation.mutateAsync(integration.connectionId)
  }

  return (
    <div
      className="flex flex-col rounded-lg border border-border bg-card p-4 cursor-pointer transition-[filter] hover:brightness-105"
      onClick={onToggle}
    >
      {/* Info row */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="size-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
            {getProviderIcon('BANK')}
          </div>

          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-foreground truncate">{displayName}</span>
              <Badge
                variant={statusVariant}
                className={cn(
                  statusVariant === 'default' &&
                    'bg-green-500/20 text-green-600 dark:text-green-400 border-green-500/30',
                  statusVariant === 'outline' &&
                    'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400 border-yellow-500/30'
                )}
              >
                {t(statusLabelKey)}
              </Badge>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
              {integration.accountId && <span className="font-mono">{integration.accountId}</span>}
              {integration.currency && <span>{integration.currency}</span>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div onClick={(e) => e.stopPropagation()}>
            <ConfirmPopover
              trigger={
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-destructive shrink-0"
                  disabled={revokeMutation.isPending}
                >
                  <Unplug className="size-4" />
                </Button>
              }
              title={t('cards.integrations.bank.disconnectTitle')}
              description={t('cards.integrations.bank.disconnectDescription')}
              confirmLabel={t('cards.integrations.bank.disconnectConfirm')}
              onConfirm={handleRevoke}
            />
          </div>
          <ChevronDown
            className={cn(
              'size-4 text-muted-foreground transition-transform duration-200',
              isExpanded && 'rotate-180'
            )}
          />
        </div>
      </div>

      {/* Animated expanded content */}
      <motion.div style={{ height: animatedHeight }} className="overflow-hidden">
        <div ref={contentRef}>
          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="pt-3 space-y-3"
              >
                {/* Error message */}
                {hasError && (
                  <div className="flex items-start gap-1.5 text-xs text-destructive">
                    <AlertTriangle className="size-3 mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{integration.lastSyncError}</span>
                  </div>
                )}

                {/* Sync info row */}
                <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                  <span className="text-xs text-muted-foreground">{lastSyncedLabel}</span>
                </div>

                {/* Sync actions */}
                {needsReconnect ? (
                  <div onClick={(e) => e.stopPropagation()}>
                    <Button type="button" variant="outline" size="sm" onClick={onReconnect}>
                      {t('cards.integrations.bank.reconnect')}
                    </Button>
                  </div>
                ) : (
                  isActive && (
                    <div
                      className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t border-border"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DateRangePicker
                        value={syncDateRange}
                        onChange={setSyncDateRange}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleSync}
                        disabled={syncMutation.isPending || !syncDateRange?.from || !syncDateRange?.to}
                        className="gap-1.5 shrink-0"
                      >
                        <RefreshCw className={cn('size-3.5', syncMutation.isPending && 'animate-spin')} />
                        {syncMutation.isPending
                          ? t('cards.integrations.sync.syncing')
                          : t('cards.integrations.sync.syncButton')}
                      </Button>
                    </div>
                  )
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  )
}

/**
 * BankConnectionsCard
 *
 * Displays bank account connections for financial data sync.
 *
 * @component
 */
export function BankConnectionsCard(): ReactElement {
  const { t } = useTranslation('features-settings')
  const integrationsQuery = useIntegrationsStatus()
  const cardRef = useRef<HTMLDivElement>(null)

  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false)
  const [reconnectCountry, setReconnectCountry] = useState<string>('PL')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // Filter to only bank connections
  const bankIntegrations = (integrationsQuery.data ?? []).filter(
    (integration) => integration.provider === BANK_PROVIDER
  )

  const handleToggle = (id: string): void => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  const handleReconnect = (): void => {
    // Default to PL for reconnect - could be enhanced to detect from existing connection
    setReconnectCountry('PL')
    setIsConnectModalOpen(true)
  }

  // Collapse all when clicking outside the card
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      const target = event.target as Node
      if (cardRef.current?.contains(target)) return
      const popoverContent = (target as Element).closest?.('[data-radix-popper-content-wrapper]')
      if (popoverContent) return
      setExpandedId(null)
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <>
      <Card ref={cardRef} className="glass-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Landmark className="size-5" />
              {t('cards.bankConnections.title', { defaultValue: 'Banking' })}
            </CardTitle>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsConnectModalOpen(true)}
              className="gap-1.5"
            >
              <Plus className="size-3.5" />
              {t('cards.integrations.connectBank')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          {integrationsQuery.isLoading ? (
            <div className="space-y-3">
              <div className="flex items-center gap-4 p-4 rounded-lg border border-border">
                <Skeleton className="size-10 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            </div>
          ) : integrationsQuery.isError ? (
            <div className="text-center py-8 text-destructive">{t('cards.integrations.loadError')}</div>
          ) : bankIntegrations.length === 0 ? (
            <div className="text-center py-8">
              <Landmark className="size-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground mb-4">
                {t('cards.bankConnections.noConnections', { defaultValue: 'No bank accounts connected' })}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsConnectModalOpen(true)}
                className="gap-1.5"
              >
                <Plus className="size-3.5" />
                {t('cards.integrations.connectFirstBank')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {bankIntegrations.map((integration, idx) => {
                const entryId = `${integration.provider}-${integration.connectionId ?? idx}`
                return (
                  <BankEntry
                    key={entryId}
                    integration={integration}
                    isExpanded={expandedId === entryId}
                    onToggle={() => handleToggle(entryId)}
                    onReconnect={handleReconnect}
                  />
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <ConnectBankModal
        open={isConnectModalOpen}
        onOpenChange={setIsConnectModalOpen}
        defaultCountry={reconnectCountry}
      />
    </>
  )
}

export default BankConnectionsCard
